
  import {mapSectionComponents} from '@/helper/SectionHelper';
  import {getMetaTags} from '@/helper/Seo';
  import LazyHydrate from 'vue-lazy-hydration';
  import {scopeHelper} from '@/helper/ScopeHelper';
  import startCase from 'lodash/startCase';

  export default {
    name: 'JobPostingDetails',
    components: {
      LazyHydrate,
    },
    layout: 'default',
    async asyncData({
      app,
      error,
      route,
      redirect,
      $cachedStrapi,
      store,
      $showJobAsPublished,
    }) {
      // Wichtig: Hier müssten wir alle Jobs bekommen unabhängig vom Zeitraum-Inaktiv-Parameter
      // Weil: Sonst wissen wir nicht ob es den Job nie gab oder nicht
      let jobPosting = {};
      let strapi_job_details = 'jobdetails';
      try {
        jobPosting = await store.dispatch(
          'api/jobPosting/getJobPostingBySlug',
          route.params.slug
        );
        if (jobPosting && !$showJobAsPublished(jobPosting.is_published)) {
          strapi_job_details = 'jobdetails-inactive';
        }
        // Differenzierung ob Redirect zu Application-Page stattfinden soll
        if (
          app.$featureToggle.toggles['job_application_landingpage_redirect'] &&
          !app.$auth.loggedIn &&
          jobPosting &&
          $showJobAsPublished(jobPosting.is_published) &&
          (route.query.utm_source === 'ebay_api' ||
            route.query.utm_source === 'multiposting' ||
            route.query.utm_source === 'multiposting_plus' ||
            route.query.utm_source === 'appcast' ||
            route.query.utm_source === 'socialads')
        ) {
          // Query parameters for application landingpage appended
          const currentQuery = route.query;
          const newQueryParam = {
            ...currentQuery,
            mtm_group: 'application-landingpage',
          };

          // Jedes zweite JobPosting soll den ChatGPT-Text in der Ausgabe haben
          if (
            app.$featureToggle.toggles[
              'job_application_landingpage_redirect_with_chatgpt'
            ] &&
            parseInt(jobPosting.job_id) % 2 === 0
          ) {
            newQueryParam.mtm_content = 'chatgpt';
          }

          const redirectRoute = app.localePath({
            name: 'job-application-slug',
            params: {slug: jobPosting.slug},
            query: newQueryParam,
          });

          // Redirect the user to the new URL (with status code 301)
          redirect(301, redirectRoute);
          return;
        } // Handling of redirects for inactivejobs
        else if (
          jobPosting &&
          !$showJobAsPublished(jobPosting.is_published) &&
          !route.query.mtm_group
        ) {
          // Query parameters for inactive jobs for appended
          const currentQuery = route.query;
          const newQueryParam = {...currentQuery, mtm_group: 'job-inactive'};

          const redirectRoute = app.localePath({
            name: 'job-slug',
            params: {slug: jobPosting.slug},
            query: newQueryParam,
          });

          // Redirect the user to the new URL (with status code 301)
          redirect(301, redirectRoute);
          return;
        }
      } catch (e) {
        // Handle different error scenarios
        if (e.response) {
          const status = e.response.status;
          const statusText = e.response.data['hydra:description'];
          const currentQuery = route.query;
          const slug = route.params.slug;
          const readableSlug = startCase(slug.replace(/-/g, ' '));

          if (status === 410 && statusText === 'JobPosting is inactive!') {
            error({statusCode: 410, message: 'JobPosting is inactive!'});
            return;
          } else if (
            status === 410 &&
            statusText === 'JobPosting is deactivated!'
          ) {
            const newQueryParam = {
              ...currentQuery,
              mtm_group: 'job-deactivated',
              search: readableSlug,
            };
            const redirectRoute = app.localePath({
              name: 'jobs',
              query: newQueryParam,
            });
            redirect(301, redirectRoute);
            return;
          } else {
            const newQueryParam = {
              ...currentQuery,
              mtm_group: 'job-not-found',
              search: readableSlug,
            };
            const redirectRoute = app.localePath({
              name: 'jobs',
              query: newQueryParam,
            });
            redirect(302, redirectRoute);
            return;
          }
        }
      }

      const page = await $cachedStrapi
        .findOne('pages', strapi_job_details)
        .catch((e) => {
          if (e.statusCode !== 404) {
            throw e;
          }
        });

      if (!scopeHelper(page, process.env.SCOPE_CANDIDATE)) {
        error({statusCode: 404, message: 'Page not found'});
        return;
      }

      // Handling of redirects for inactive and not found jobs

      let sections;
      let metaData;
      if (jobPosting) {
        sections = await mapSectionComponents(
          page.sections,
          async function (section) {
            switch (section.component) {
              case 'SectionHomepageJobBoxWithTabs':
                section.activity = jobPosting.activity;
                break;

              case 'SectionJobDetails':
                section.jobPosting = jobPosting;
                break;

              case 'SectionBreadcrumbs':
                section.breadcrumbs = [
                  {
                    text: app.i18n.t('page.index'),
                    disabled: false,
                    to: app.localePath({
                      name: 'index',
                    }),
                    nuxt: true,
                    exact: true,
                  },
                  {
                    text: app.i18n.t('page.jobs'),
                    disabled: false,
                    to: app.localePath(
                      store.getters['breadcrumbs/hasJobListingRoute']
                        ? store.state.breadcrumbs.lastJobListingRoute
                        : {
                            name: 'jobs',
                          }
                    ),
                    nuxt: true,
                    exact: true,
                  },
                  {
                    text: jobPosting.name,
                    disabled: true,
                    to: app.localePath({
                      name: 'job-slug',
                      params: {
                        slug: jobPosting.slug,
                      },
                    }),
                    nuxt: true,
                    exact: true,
                  },
                ];
                break;

              case 'SectionSeoContent':
                if (
                  section.seo_content_config &&
                  section.seo_content_config.seo_contents &&
                  section.seo_content_config.seo_contents[0]
                ) {
                  section.seoContent = await $cachedStrapi.findOne(
                    'seo-contents',
                    section.seo_content_config.seo_contents[0].id
                  );
                }
                break;
            }
          }
        );

        // Meta-Data mit Werten vom Job
        metaData = page && page.meta ? page.meta : {};

        // Meta-Data von ChatGPT wenn voranden
        if (
          app.$featureToggle.toggles['job_posting_chatgpt_meta_data'] &&
          jobPosting.chat_gpt.job_posting_seo_meta
        ) {
          metaData.title = jobPosting.chat_gpt.job_posting_seo_meta.meta_title;
          metaData.description =
            jobPosting.chat_gpt.job_posting_seo_meta.meta_description;

          // Aufbereitung der Keywords als String
          const keywordsObj =
            jobPosting.chat_gpt.job_posting_seo_meta.meta_keywords;
          const keywordsArray = Object.values(keywordsObj);
          // Entfernen von nicht-String-Elementen wie 'hydra:view'
          const filteredKeywords = keywordsArray.filter(
            (keyword) => typeof keyword === 'string'
          );

          metaData.keywords = filteredKeywords.join(', ');
        } else {
          metaData.title = jobPosting.name;
          metaData.description = jobPosting.short_description;
        }
      } else {
        sections = await mapSectionComponents(
          page.sections,
          async function (section) {
            switch (section.component) {
              case 'SectionBreadcrumbs':
                section.breadcrumbs = [
                  {
                    text: app.i18n.t('page.index'),
                    disabled: false,
                    to: app.localePath({
                      name: 'index',
                    }),
                    nuxt: true,
                    exact: true,
                  },
                  {
                    text: app.i18n.t('page.jobs'),
                    disabled: false,
                    to: app.localePath(
                      store.getters['breadcrumbs/hasJobListingRoute']
                        ? store.state.breadcrumbs.lastJobListingRoute
                        : {
                            name: 'jobs',
                          }
                    ),
                    nuxt: true,
                    exact: true,
                  },
                  {
                    text: app.i18n.t('page.job_not_found'),
                    disabled: true,
                    to: app.localePath({
                      name: 'job-slug',
                      params: {
                        slug: 'job-not-found',
                      },
                    }),
                    nuxt: true,
                    exact: true,
                  },
                ];
                break;

              case 'SectionSeoContent':
                if (
                  section.seo_content_config &&
                  section.seo_content_config.seo_contents &&
                  section.seo_content_config.seo_contents[0]
                ) {
                  section.seoContent = await $cachedStrapi.findOne(
                    'seo-contents',
                    section.seo_content_config.seo_contents[0].id
                  );
                }
                break;
            }
          }
        );

        // Meta-Data mit Werten vom Job
        metaData = page && page.meta ? page.meta : {};
      }

      return {
        metaData,
        sections,
        jobPosting,
      };
    },
    data() {
      return {
        metaData: {},
        sections: [],
        jobPosting: {},
      };
    },
    jsonld() {
      // return/no jsonld if job is not published
      if (!this.jobPosting || !this.jobPosting.is_published) {
        return null;
      }

      // prepare values
      let description = this.jobPosting.description;
      if (this.jobPosting.requirement) {
        description +=
          '\r\n\r\nAnforderungsprofil:\r\n\r\n' + this.jobPosting.requirement;
      }

      const jobPostingLocations = [];
      for (const key in this.jobPosting.locations) {
        if (key === 'hydra:view') {
          continue;
        }

        const location = this.jobPosting.locations[key];
        jobPostingLocations.push({
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: location.city,
            postalCode: location.zipcode,
            addressRegion: 'nicht bekannt',
            streetAddress: 'nicht bekannt',
            addressCountry: 'DE',
          },
        });
      }

      if (jobPostingLocations.length === 0) {
        jobPostingLocations.push({
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'bundesweit',
            postalCode: '00000',
            addressRegion: 'nicht bekannt',
            streetAddress: 'nicht bekannt',
            addressCountry: 'DE',
          },
        });
      }

      const jobPostingBaseSalary = {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
      };

      if (this.jobPosting.salary_type === 'auf Anfrage') {
        jobPostingBaseSalary.value = {
          '@type': 'QuantitativeValue',
          value: 'auf Anfrage',
          unitText: 'HOUR',
        };
      } else {
        jobPostingBaseSalary.value = {
          '@type': 'QuantitativeValue',
          value: this.jobPosting.salary,
          unitText: this.jobPosting.salary_type === 'pro Tag' ? 'DAY' : 'HOUR',
        };
      }

      return {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: this.jobPosting.name,
        description: description,
        datePosted: this.jobPosting.date,
        employmentType: ['CONTRACTOR', 'TEMPORARY'],
        hiringOrganization: {
          '@type': 'Organization',
          name: this.jobPosting.company,
          logo: 'https://www.promotionbasis.de/PB_Logo.png',
        },
        identifier: {
          '@type': 'Organization',
          name: this.jobPosting.company,
          value: this.jobPosting.number,
        },
        baseSalary: jobPostingBaseSalary,
        occupationalCategory: this.jobPosting.activity,
        industry: this.jobPosting.industry,
        jobLocation: jobPostingLocations,

        jobStartDate: this.jobPosting.schema_data.job_start_date,
        validThrough: this.jobPosting.schema_data.valid_through,
        jobImmediateStart: this.jobPosting.schema_data.job_immediate_start,
      };
    },
    head() {
      const metaData = getMetaTags(this.metaData);

      // Für Ausgabe wenn Job nicht gefunden
      if (!this.jobPosting) {
        metaData.push({
          name: 'robots',
          content: 'noindex',
        });
        metaData.push({
          name: 'googlebot',
          content: 'noindex',
        });

        return {
          title: this.metaData.title,
          meta: metaData,
        };
      }

      // When JobPosting has OpenGraph-Image first reset/remove the default image
      if (this.jobPosting.open_graph_image) {
        this.metaData.share_image = null;
      }

      if (this.jobPosting.open_graph_image) {
        metaData.push(
          {
            hid: 'image',
            name: 'image',
            content:
              process.env.NUXT_ENV_LEGACY_OPEN_GRAPH_IMAGE_URL +
              this.jobPosting.open_graph_image,
          },
          {
            hid: 'og:image',
            property: 'og:image',
            content:
              process.env.NUXT_ENV_LEGACY_OPEN_GRAPH_IMAGE_URL +
              this.jobPosting.open_graph_image,
          },
          {
            hid: 'twitter:image',
            name: 'twitter:image',
            content:
              process.env.NUXT_ENV_LEGACY_OPEN_GRAPH_IMAGE_URL +
              this.jobPosting.open_graph_image,
          }
        );
      }

      return {
        title: this.metaData.title,
        meta: metaData,
      };
    },
  };
